import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  CloudUpload,
  X,
  Clock,
  DollarSign,
  Tag,
  Globe,
  Video,
  Upload,
  Ticket,
  Link as LinkIcon
} from 'lucide-react';
import { eventsService, digitalOceanService } from '../services/api';

const AdminEventCreate = () => {
  const navigate = useNavigate();
  const [eventTypes, setEventTypes] = useState([]);
  const [artists, setArtists] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [uploadProgress, setUploadProgress] = useState({
    cover: { progress: 0, status: 'idle', fileName: '', error: null }
  });
  
  const [uploadedFiles, setUploadedFiles] = useState({
    coverUrl: '',
    coverPreview: ''
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    event_type: '',
    hosts: [],
    featured_artists: [],
    venue_name: '',
    venue_address: '',
    city: '',
    country: 'Ghana',
    is_virtual: false,
    virtual_link: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    start_time: '19:00',
    end_time: '',
    timezone: 'Africa/Accra',
    ticket_type: 'paid',
    price: '',
    early_bird_price: '',
    early_bird_end: '',
    vip_price: '',
    capacity: '',
    preview_video_url: '',
    map_embed: '',
    status: 'approved',
    tags: [],
    social_links: {
      facebook: '',
      twitter: '',
      instagram: ''
    }
  });

  const [ticketTypes, setTicketTypes] = useState([
    { id: 'general', name: 'General Admission', price: '', quantity: '', description: '' }
  ]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesResponse, artistsResponse] = await Promise.all([
          eventsService.getEventTypes(),
          eventsService.getUsers()
        ]);
        
        console.log('Event types:', typesResponse);
        console.log('Users:', artistsResponse);
        
        setEventTypes(typesResponse || []);
        setUsers(artistsResponse || []);
        
        // Try to fetch artists if needed
        try {
          const artistsResp = await eventsService.getArtists?.() || [];
          setArtists(artistsResp || []);
        } catch (artistError) {
          console.log('No artists endpoint available');
          setArtists([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set fallback data
        setEventTypes([
          { id: 1, name: 'Concert' },
          { id: 2, name: 'Festival' },
          { id: 3, name: 'Workshop' },
          { id: 4, name: 'Meetup' },
          { id: 5, name: 'Virtual' },
        ]);
        setUsers([]);
        setArtists([]);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else if (name.startsWith('social_')) {
      const platform = name.split('_')[1];
      setFormData({
        ...formData,
        social_links: {
          ...formData.social_links,
          [platform]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleHostChange = (userId) => {
    setFormData(prev => {
      const isSelected = prev.hosts.includes(userId);
      if (isSelected) {
        return {
          ...prev,
          hosts: prev.hosts.filter(id => id !== userId)
        };
      } else {
        return {
          ...prev,
          hosts: [...prev.hosts, userId]
        };
      }
    });
  };

  const handleArtistChange = (artistId) => {
    setFormData(prev => {
      const isSelected = prev.featured_artists.includes(artistId);
      if (isSelected) {
        return {
          ...prev,
          featured_artists: prev.featured_artists.filter(id => id !== artistId)
        };
      } else {
        return {
          ...prev,
          featured_artists: [...prev.featured_artists, artistId]
        };
      }
    });
  };

  const handleTicketTypeChange = (index, field, value) => {
    const updated = [...ticketTypes];
    updated[index][field] = value;
    setTicketTypes(updated);
  };

  const addTicketType = () => {
    setTicketTypes([
      ...ticketTypes,
      { id: Date.now().toString(), name: '', price: '', quantity: '', description: '' }
    ]);
  };

  const removeTicketType = (index) => {
    if (ticketTypes.length > 1) {
      const updated = [...ticketTypes];
      updated.splice(index, 1);
      setTicketTypes(updated);
    }
  };

  // Handle cover upload
  const handleCoverUpload = async (file) => {
    if (!file) return;

    try {
      setUploadProgress(prev => ({
        ...prev,
        cover: { progress: 0, status: 'uploading', fileName: file.name, error: null }
      }));

      const previewUrl = URL.createObjectURL(file);
      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: previewUrl
      }));

      const uploadData = await digitalOceanService.generateEventCoverUploadURL({
        filename: file.name
      });

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to get upload URL');
      }

      await digitalOceanService.uploadToSpaces(
        uploadData.upload_url,
        uploadData.fields,
        file
      );

      setUploadProgress(prev => ({
        ...prev,
        cover: { ...prev.cover, status: 'success', progress: 100 }
      }));

      setUploadedFiles(prev => ({
        ...prev,
        coverUrl: uploadData.public_url
      }));

    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        cover: { 
          ...prev.cover, 
          status: 'error', 
          error: error.message 
        }
      }));

      if (uploadedFiles.coverPreview) {
        URL.revokeObjectURL(uploadedFiles.coverPreview);
      }

      setUploadedFiles(prev => ({
        ...prev,
        coverPreview: ''
      }));
    }
  };

  const handleFileSelect = (type) => (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (type === 'cover') {
      handleCoverUpload(file);
    }
  };

  const clearCoverImage = () => {
    if (uploadedFiles.coverPreview) {
      URL.revokeObjectURL(uploadedFiles.coverPreview);
    }
    
    setUploadedFiles({
      coverUrl: '',
      coverPreview: ''
    });
    
    setUploadProgress(prev => ({
      ...prev,
      cover: { progress: 0, status: 'idle', fileName: '', error: null }
    }));
  };

  const validateForm = () => {
    const required = ['title', 'description', 'venue_name', 'venue_address', 'city', 'start_date', 'start_time'];
    
    for (const field of required) {
      if (!formData[field]) {
        alert(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }

    if (!uploadedFiles.coverUrl) {
      alert('Please upload a cover image');
      return false;
    }

    if (formData.ticket_type === 'paid' && !formData.price && ticketTypes.every(t => !t.price)) {
      alert('Please set a price for paid tickets');
      return false;
    }

    if (formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      alert('End date cannot be before start date');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Process ticket types
      const validTicketTypes = ticketTypes
        .filter(t => t.name && t.price)
        .map(t => ({
          name: t.name,
          price: parseFloat(t.price) || 0,
          quantity: t.quantity ? parseInt(t.quantity) : 0,
          description: t.description || ''
        }));

      // Ensure cover URL has proper protocol
      let coverImageUrl = uploadedFiles.coverUrl;
      if (coverImageUrl && !coverImageUrl.startsWith('http')) {
        coverImageUrl = `https://${coverImageUrl}`;
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        short_description: formData.short_description || '',
        event_type: formData.event_type || null,
        hosts: formData.hosts.map(id => parseInt(id)).filter(id => !isNaN(id)),
        featured_artists: formData.featured_artists.map(id => parseInt(id)).filter(id => !isNaN(id)),
        venue_name: formData.venue_name,
        venue_address: formData.venue_address,
        city: formData.city,
        country: formData.country || 'Ghana',
        is_virtual: formData.is_virtual || false,
        virtual_link: formData.virtual_link || '',
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        start_time: formData.start_time,
        end_time: formData.end_time || null,
        timezone: formData.timezone || 'Africa/Accra',
        ticket_type: formData.ticket_type,
        price: formData.price ? parseFloat(formData.price) : 0,
        early_bird_price: formData.early_bird_price ? parseFloat(formData.early_bird_price) : null,
        early_bird_end: formData.early_bird_end || null,
        vip_price: formData.vip_price ? parseFloat(formData.vip_price) : null,
        capacity: formData.capacity ? parseInt(formData.capacity) : 0,
        preview_video_url: formData.preview_video_url || '',
        map_embed: formData.map_embed || '',
        cover_image_url: coverImageUrl,
        status: formData.status || 'approved',
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        social_links: formData.social_links,
        ticket_types: validTicketTypes
      };

      console.log('Submitting event data:', eventData);
      const response = await eventsService.adminCreateEvent(eventData);
      
      alert(`✅ Event created successfully!`);
      navigate(`/admin/events`);
      
    } catch (error) {
      console.error('❌ Error creating event:', error);
      alert(`Creation failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const UploadProgressBar = ({ type, progress, fileName, status, error }) => {
    return (
      <div className="mt-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-700 truncate">
            {fileName}
          </div>
          <div className="text-sm text-gray-500">
            {status === 'uploading' ? `${progress}%` : status}
          </div>
        </div>
        
        {status === 'uploading' && (
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        
        {status === 'success' && (
          <div className="flex items-center text-green-600 text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Upload complete
          </div>
        )}
        
        {status === 'error' && (
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            {error}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/events')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Event</h1>
                <p className="text-gray-600 font-medium">
                  Create a new event on the platform
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Event Form */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8">
            <div className="space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Basic Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      value={formData.title}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      placeholder="Enter event title"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Description
                    </label>
                    <input
                      type="text"
                      name="short_description"
                      value={formData.short_description}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      placeholder="Brief description (max 300 characters)"
                      maxLength="300"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Description *
                    </label>
                    <textarea
                      name="description"
                      required
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                      placeholder="Describe the event in detail..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type
                    </label>
                    <select
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    >
                      <option value="">Select Type</option>
                      {eventTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      placeholder="music, concert, live, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Date & Time
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      required
                      value={formData.start_date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      name="start_time"
                      required
                      value={formData.start_time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Time
                    </label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    >
                      <option value="Africa/Accra">Africa/Accra (GMT)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Location
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <input
                      type="checkbox"
                      id="is_virtual"
                      name="is_virtual"
                      checked={formData.is_virtual}
                      onChange={handleChange}
                      className="h-4 w-4 text-[#1d1d1d]"
                    />
                    <label htmlFor="is_virtual" className="text-sm font-medium text-gray-700">
                      This is a virtual/online event
                    </label>
                  </div>

                  {!formData.is_virtual ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Venue Name *
                        </label>
                        <input
                          type="text"
                          name="venue_name"
                          required={!formData.is_virtual}
                          value={formData.venue_name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="Enter venue name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          required={!formData.is_virtual}
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="Enter city"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Venue Address *
                        </label>
                        <textarea
                          name="venue_address"
                          required={!formData.is_virtual}
                          value={formData.venue_address}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none"
                          placeholder="Enter full address"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Map Embed Code (Optional)
                        </label>
                        <textarea
                          name="map_embed"
                          value={formData.map_embed}
                          onChange={handleChange}
                          rows="2"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors resize-none font-mono text-sm"
                          placeholder="Paste Google Maps embed code here..."
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Virtual Event Link *
                      </label>
                      <input
                        type="url"
                        name="virtual_link"
                        required={formData.is_virtual}
                        value={formData.virtual_link}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* People */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  People
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Hosts */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hosts (Optional)
                    </label>
                    <div className="space-y-2">
                      {users.map((user) => (
                        <label
                          key={user.id}
                          className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                            formData.hosts.includes(user.id)
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.hosts.includes(user.id)}
                            onChange={() => handleHostChange(user.id)}
                            className="mr-3 h-4 w-4 text-blue-600"
                          />
                          <div>
                            <div className="text-sm font-medium">{user.username}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Featured Artists */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Artists (Optional)
                    </label>
                    <div className="space-y-2">
                      {artists.map((artist) => (
                        <label
                          key={artist.id}
                          className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                            formData.featured_artists.includes(artist.id)
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.featured_artists.includes(artist.id)}
                            onChange={() => handleArtistChange(artist.id)}
                            className="mr-3 h-4 w-4 text-blue-600"
                          />
                          <Users className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-sm">{artist.stage_name || artist.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Media */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ImageIcon className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Media
                </h3>
                
                <div className="space-y-6">
                  {/* Cover Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Image *
                    </label>
                    
                    {/* Image Preview */}
                    {(uploadedFiles.coverPreview || uploadedFiles.coverUrl) && (
                      <div className="mb-4 relative">
                        <div className="relative w-40 h-40 mx-auto">
                          <img
                            src={uploadedFiles.coverPreview || uploadedFiles.coverUrl}
                            alt="Cover preview"
                            className="w-full h-full object-cover rounded-lg shadow-md border border-gray-300"
                          />
                          
                          {/* Clear Button */}
                          <button
                            type="button"
                            onClick={clearCoverImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <input
                        type="file"
                        id="cover-upload"
                        accept="image/*"
                        onChange={handleFileSelect('cover')}
                        className="hidden"
                      />
                      <label
                        htmlFor="cover-upload"
                        className="cursor-pointer block"
                      >
                        {uploadedFiles.coverPreview ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <Upload className="h-8 w-8 text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-600">
                              Replace cover image
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <ImageIcon className="h-10 w-10 text-gray-400 mx-auto" />
                            <div>
                              <p className="text-sm font-medium text-gray-700">Upload Cover Image</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Click or drag to upload
                              </p>
                            </div>
                          </div>
                        )}
                      </label>
                    </div>
                    
                    {uploadProgress.cover.status !== 'idle' && (
                      <UploadProgressBar
                        type="cover"
                        progress={uploadProgress.cover.progress}
                        fileName={uploadProgress.cover.fileName}
                        status={uploadProgress.cover.status}
                        error={uploadProgress.cover.error}
                      />
                    )}
                  </div>

                  {/* Preview Video */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview Video URL (Optional)
                    </label>
                    <div className="flex items-center space-x-3">
                      <Video className="h-5 w-5 text-gray-400" />
                      <input
                        type="url"
                        name="preview_video_url"
                        value={formData.preview_video_url}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="YouTube or Vimeo URL"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Add a YouTube or Vimeo video for event preview
                    </p>
                  </div>
                </div>
              </div>

              {/* Ticketing */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Ticket className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Ticketing
                </h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ticket Type *
                      </label>
                      <select
                        name="ticket_type"
                        value={formData.ticket_type}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      >
                        <option value="paid">Paid</option>
                        <option value="free">Free</option>
                        <option value="invite">Invite Only</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Capacity
                      </label>
                      <input
                        type="number"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="0 for unlimited"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                      >
                        <option value="Africa/Accra">Africa/Accra</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                  </div>

                  {/* Main Pricing */}
                  {formData.ticket_type === 'paid' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Regular Price (GHS)
                          </label>
                          <input
                            type="number"
                            name="price"
                            step="0.01"
                            value={formData.price}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Early Bird Price (GHS)
                          </label>
                          <input
                            type="number"
                            name="early_bird_price"
                            step="0.01"
                            value={formData.early_bird_price}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                            placeholder="Optional"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Early Bird Ends
                          </label>
                          <input
                            type="datetime-local"
                            name="early_bird_end"
                            value={formData.early_bird_end}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          VIP Price (GHS)
                        </label>
                        <input
                          type="number"
                          name="vip_price"
                          step="0.01"
                          value={formData.vip_price}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                          placeholder="Optional"
                        />
                      </div>
                    </>
                  )}

                  {/* Ticket Types */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Ticket Types
                      </label>
                      <button
                        type="button"
                        onClick={addTicketType}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        + Add Ticket Type
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {ticketTypes.map((ticket, index) => (
                        <div key={ticket.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-900">Ticket Type {index + 1}</h4>
                            {ticketTypes.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTicketType(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Name
                              </label>
                              <input
                                type="text"
                                value={ticket.name}
                                onChange={(e) => handleTicketTypeChange(index, 'name', e.target.value)}
                                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#1d1d1d] text-sm"
                                placeholder="General Admission"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Price (GHS)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={ticket.price}
                                onChange={(e) => handleTicketTypeChange(index, 'price', e.target.value)}
                                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#1d1d1d] text-sm"
                                placeholder="0.00"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Quantity
                              </label>
                              <input
                                type="number"
                                value={ticket.quantity}
                                onChange={(e) => handleTicketTypeChange(index, 'quantity', e.target.value)}
                                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#1d1d1d] text-sm"
                                placeholder="0 for unlimited"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Description
                              </label>
                              <input
                                type="text"
                                value={ticket.description}
                                onChange={(e) => handleTicketTypeChange(index, 'description', e.target.value)}
                                className="w-full px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#1d1d1d] text-sm"
                                placeholder="Optional"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-[#1d1d1d]" />
                  Social Links (Optional)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facebook
                    </label>
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">fb.com/</span>
                      <input
                        type="text"
                        name="social_facebook"
                        value={formData.social_links.facebook}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="username"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter
                    </label>
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">@</span>
                      <input
                        type="text"
                        name="social_twitter"
                        value={formData.social_links.twitter}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="username"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instagram
                    </label>
                    <div className="flex items-center space-x-3">
                      <span className="text-gray-500">@</span>
                      <input
                        type="text"
                        name="social_instagram"
                        value={formData.social_links.instagram}
                        onChange={handleChange}
                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                        placeholder="username"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Submit */}
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Initial Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-colors"
                    >
                      <option value="approved">Approved (Publish Immediately)</option>
                      <option value="pending">Pending Review</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white py-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Creating Event...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4 mr-2" />
                          Create Event
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminEventCreate;